import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Box } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const phoneScreen = { height: 932, width: 430 };
const bezel = 14;
const stageMargin = 24;

export const getFrameScale = (windowHeight: number) => (
  Math.min(1, (windowHeight - 2 * stageMargin) / (phoneScreen.height + 2 * bezel))
);

// The phone loads the same dashboard link without the notification id (`n`), so a visit opened
// from a notification is only recorded once, by the page around the phone.
export const getEmbeddedDashboardUrl = (href: string) => {
  const url = new URL(href);
  const [route, query = ''] = url.hash.split('?');
  const params = new URLSearchParams(query);
  params.delete('n');
  const remainingQuery = params.toString();
  url.hash = remainingQuery ? `${route}?${remainingQuery}` : route;
  return url.toString();
};

interface PhoneFrameProps {
  tint?: string,
}

// The dashboard runs inside a real 430px-wide embedded window, so breakpoints, viewport
// heights, sticky elements, pop-ups and scrolling behave exactly as on a phone.
export const PhoneFrame = ({ tint }: PhoneFrameProps) => {
  const { t } = useTranslation();
  const { windowHeight } = useMediaQuery();
  const [embeddedUrl] = useState(() => getEmbeddedDashboardUrl(window.location.href));

  return (
    <Box
      data-testid="crd-frame-stage"
      sx={{
        alignItems: 'center',
        bgcolor: tint ?? 'background.default',
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        data-testid="crd-frame-device"
        sx={{
          bgcolor: 'grey.900',
          borderRadius: '56px',
          flexShrink: 0,
          p: `${bezel}px`,
          transform: `scale(${getFrameScale(windowHeight)})`,
          transformOrigin: 'center',
        }}
      >
        <Box
          component="iframe"
          data-testid="crd-frame-screen"
          src={embeddedUrl}
          sx={{
            bgcolor: 'background.paper',
            border: 0,
            borderRadius: '42px',
            display: 'block',
            height: phoneScreen.height,
            width: phoneScreen.width,
          }}
          title={t($ => $.customerDashboard.desktopFrame.title)}
        />
      </Box>
    </Box>
  );
};
